import { useMutation, useQuery } from "@tanstack/react-query";

import { ApiError, apiFetchJson } from "@rook/shared/lib/apiFetch";
import type { Environment } from "@rook/shared/lib/appContext";

import type { HealthPillarConfig } from "./types";

/**
 * Fetches the customer's `HealthPillarConfig` from
 * `GET /client/key_selector`. The Simulator uses it to look up
 * `excluded_keys` per pillar/structure so the generated JSON
 * matches what the customer's webhook would actually receive
 * (after their Data Structure config strips out the fields they
 * opted out of).
 *
 * (Earlier v3 hit `/customize/key_configuration` — a path that DOES
 * NOT EXIST on rook_clients_api. API Gateway returned the SigV4
 * "Missing Authentication Token" error on every Simulator load.
 * Canonical path matches legacy `keysConfigActions.fetchKeysConfiguration`.
 * Diagnosed 2026-05-23.)
 *
 * Treats 404 as "no config customised yet" → returns null. The
 * Simulator's submit handler bails out with an inline error when
 * the value is null, so the customer is told to configure
 * Data Structure first.
 */
const keyConfigQueryKey = (env: Environment) =>
  ["simulator", "key-config", env] as const;

const keyConfigPath = (env: Environment) =>
  `/api/proxy/clients/${env}/client/key_selector`;

export const useKeyConfig = (env: Environment) =>
  useQuery({
    queryKey: keyConfigQueryKey(env),
    queryFn: async () => {
      try {
        return await apiFetchJson<HealthPillarConfig>(keyConfigPath(env));
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
  });

/**
 * Sends the cleaned simulator document to the customer's data
 * webhook via `POST /client/send_webhook`. The backend resolves
 * the real customer + auth from the session cookie carried
 * through the proxy Lambda; no per-call auth wiring needed here.
 *
 * Mutation (not query) because each click is a discrete event;
 * no caching, no refetch on focus.
 */
const sendHookPath = (env: Environment) =>
  `/api/proxy/clients/${env}/client/send_webhook`;

export const useSendHook = (env: Environment) =>
  useMutation({
    mutationFn: (document: unknown) =>
      apiFetchJson<{ ok?: boolean }>(sendHookPath(env), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(document),
      }),
  });
